import React from 'react';
import {FilterName} from './FilterControl';
import _ from 'lodash';
import {useScoringRun} from '../../../context/ScoringRunContext';
import {useTags} from '../../../hooks/api/tags';
import {useAccountView} from '../AccountGrid';
import {
  useIsMyAccountsView,
  useUserControls,
} from '../AccountGrid.controlstate';

export type FilterGroup = {
  availableOptions: FilterName[];
  filters: Array<FilterName | undefined>;
  setFilters: React.Dispatch<
    React.SetStateAction<Array<FilterName | undefined>>
  >;
  setFilterName: (filterName: FilterName | undefined, index: number) => void;
  addFilterControl: () => void;
  removeFilterControl: (index: number) => void;
  resetFilters: () => void;
};

export const useFilterGroup = () => {
  const accountView = useAccountView();
  const isMyAccountsView = useIsMyAccountsView();
  const scoringRun = useScoringRun();
  const lookalikesWithLogos = scoringRun?.lookalikesWithLogos;
  const tags = useTags({});

  const allOptions = React.useMemo(() => {
    return [
      'signals',
      'categories',
      'audiences',
      isMyAccountsView && accountView !== 'crm' && 'inCrm',
      isMyAccountsView && 'inSam',
      isMyAccountsView && !!tags.data?.length && 'tags',
      'employees',
      !!lookalikesWithLogos?.length && 'similarAccounts',
    ].filter(Boolean) as FilterName[];
  }, [accountView, isMyAccountsView, lookalikesWithLogos, tags.data]);

  const initialFilters = useInitialFilters();
  const [filters, setFilters] = React.useState<Array<FilterName | undefined>>(
    initialFilters.length > 0 ? initialFilters : [undefined]
  );

  const availableOptions = React.useMemo(() => {
    return _.difference(allOptions, filters.filter(Boolean) as FilterName[]);
  }, [allOptions, filters]);

  const addFilterControl = () => {
    setFilters((filters) => [...filters, undefined]);
  };

  const removeFilterControl = (index: number) => {
    setFilters((filters) => {
      const newFilters = [...filters];
      newFilters.splice(index, 1);
      return newFilters;
    });
  };

  const {resetFilters: resetUserControls} = useUserControls();

  const resetFilters = () => {
    setFilters([undefined]);
    resetUserControls();
  };

  const setFilterName = (filterName: FilterName | undefined, index: number) => {
    setFilters((filters) => {
      const newFilters = [...filters];
      newFilters[index] = filterName;
      return newFilters;
    });
  };

  return {
    availableOptions,
    filters,
    setFilters,
    setFilterName,
    addFilterControl,
    removeFilterControl,
    resetFilters,
  } satisfies FilterGroup;
};

const useInitialFilters = () => {
  const {
    inCrm,
    inSam,
    minEmployees,
    maxEmployees,
    signals,
    primaryCategory,
    audience,
    tags,
    similarTo,
  } = useUserControls();

  return React.useMemo(() => {
    return [
      inCrm !== undefined && 'inCrm',
      inSam !== undefined && 'inSam',
      minEmployees !== undefined && 'employees',
      maxEmployees !== undefined && 'employees',
      signals.some((s) => s.length > 0) && 'signals',
      primaryCategory.length && 'categories',
      audience.length && 'audiences',
      tags.length && 'tags',
      similarTo.length && 'similarAccounts',
    ].filter(Boolean) as FilterName[];
  }, [
    inCrm,
    inSam,
    minEmployees,
    maxEmployees,
    signals,
    primaryCategory.length,
    audience.length,
    tags.length,
    similarTo.length,
  ]);
};
